<template>
	<router-link
		v-if="product.active"
		:to="productRoute"
		class="py-6 bg-white hover:text-black"
	>
		<div
			class="flex w-full min-card-width"
			:class="{ disabled : orderingDisabled }"
		>
			<div class="flex-shrink-0 w-24 px-0 ml-3 border border-gray-300 rounded-l xl:w-32 md:mx-0">
				<WwImage
					:src="imageFile"
					classes="rounded-l"
					object-cover
					:alt="product.name"
					:fallback-image="productPlaceholderImage"
					:width="130"
					:height="130"
					image-type="menu"
				/>
			</div>
			<div class="grid w-full px-0 py-0">
				<div class="relative flex items-center w-full px-3 align-center min-pricing-width">
					<h4 class="m-0 mr-3 text-lg font-semibold text-black line-clamp-2">
						{{ product.name }}
					</h4>
				</div>

				<div class="h-4">
					<div
						v-if="hasBranding"
						class="text-sm"
					>
						by <span class="font-bold">
							{{ product.brand }}
						</span>
					</div>
				</div>

				<!-- Multi-Option Pricing -->
				<template v-if="hasPricing && isMounted">
					<div
						v-if="pricingIsArray"
						class="relative my-auto overflow-x-auto overflow-y-hidden hide-horizontal-scrollbar"
						:class="orderingDisabled ? 'pl-3' : 'pr-2'"
					>
						<WwSideScroller
							scroller-class="pt-2 pr-2 hide-horizontal-scrollbar"
							item-spacing="space-x-3"
							:gradient-width="64"
							:overscroll-limit="10"
							fade-visibility=""
							:class="{ 'mx-1': !orderingDisabled }"
						>
							<button
								v-for="{ price, unit, index } in pricePerUnit"
								:key="index"
								:class="[
									getPriceButtonBorderClasses(),
									'flex flex-col button-no-styles min-w-20 md:min-w-24 p-2 px-4 relative text-black group transition-all duration-100 ease-in-out',
									{ 'ml-2': !orderingDisabled },
									sizesInCart.includes(index) ?
										'bg-green-100 border-green-500 xl:hover:border-red-500 xl:hover:bg-red-100' :
										'hover:border-green-500 border-gray-400 bg-white'
								]"
								@click.prevent="handleAddToCart({ index })"
							>
								<div
									v-if="!orderingDisabled"
									class="absolute top-0 mt-1 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full right-position"
								>
									<div class="flex justify-center w-5 h-5 transition-all duration-100 ease-in-out bg-white border border-gray-400 rounded-full shadow xl:group-hover:shadow-lg xl:group-hover:scale-110">
										<PlusIcon
											v-if="!sizesInCart.includes(index)"
											class="m-auto text-gray-500"
										/>
										<MinusIcon
											v-else
											class="m-auto text-black transition-all duration-100 ease-in-out group-hover:text-red-500"
										/>
									</div>
								</div>
								<LoadingSpinner
									v-if="itemBeingAddedToCart == index"
									class="absolute top-0 left-0 w-full h-full p-2 bg-white opacity-75"
								/>
								<span class="font-bold">
									${{ price }}
								</span>
								<span class="text-sm text-gray-500">
									{{ formatUnit(unit) }}
								</span>
							</button>
						</WwSideScroller>
					</div>

					<!-- Single-Option Pricing -->
					<div
						v-else
						class="flex w-full pt-2 pl-2 mb-3"
					>
						<button
							:class="[
								getPriceButtonBorderClasses(),
								'flex flex-col button-no-styles min-w-20 md:min-w-24 p-2 px-3 mx-1 relative group transition-all duration-100 ease-in-out',
								sizesInCart.includes(0) ? 'bg-green-100 border-green-500 xl:hover:border-red-500 xl:hover:bg-red-100' : 'hover:border-green-500 border-gray-400 bg-white',
							]"
							@click.prevent="handleAddToCart({ index: 0 })"
						>
							<div
								v-if="!orderingDisabled"
								class="absolute top-0 transform -translate-x-1/2 -translate-y-1/2 bg-white right-position"
							>
								<div class="flex justify-center w-5 h-5 transition-all duration-100 ease-in-out bg-white border border-gray-400 rounded-full xl:group-hover:scale-110">
									<PlusIcon
										v-if="!sizesInCart.includes(0)"
										class="m-auto text-gray-500"
									/>
									<MinusIcon
										v-else
										class="m-auto text-black transition-all duration-100 ease-in-out group-hover:text-red-500"
									/>
								</div>
							</div>

							<LoadingSpinner
								v-if="itemBeingAddedToCart === 0"
								class="absolute top-0 left-0 w-full h-full p-2 bg-white opacity-75"
							/>
							<div class="font-bold">
								${{ price }}
							</div>
							<div class="text-sm text-gray-500">
								ea
							</div>
						</button>
					</div>
				</template>
			</div>
		</div>
	</router-link>
</template>

<script>
import Bugsnag from '@bugsnag/js'
import {
	mapActions,
	mapGetters,
	mapMutations,
	mapState
} from 'vuex'

import MinusIcon from '@/components/icons/MinusIcon.vue'
import PlusIcon from '@/components/icons/PlusIcon.vue'
import LoadingSpinner from '@/components/multiUse/LoadingSpinner.vue'
import WwSideScroller from '@/components/UI/WwSideScroller.vue'
import { CONTENT_SERVER } from '@/constants'
import { DELETE_FROM_CART_MODAL } from '@/constants/modal/names'
import {
	formatUnit,
	getPrice,
	getPriceButtonBorderClasses,
	getPricePerUnit,
	hasPricing,
	pricingIsArray
} from '@/utils/pricing'
import getProductRouteName from '@/utils/product/get-route-name.js'

export default {
	components: {
		LoadingSpinner,
		WwImage: () => import('@/components/multiUse/WwImage'),
		WwSideScroller,
		PlusIcon,
		MinusIcon
	},
	props: {
		product: {
			type: Object,
			default: () => ({})
		},
		indication: {
			type: String,
			default: ''
		},
		splitIndication: {
			type: Boolean
		}
	},
	data() {
		return {
			itemBeingAddedToCart: null,
			selected: '22',
			counter: 1,
			size: '',
			isMounted: false,
			sizesInCart: []
		}
	},
	computed: {
		...mapState([ 'business' ]),
		productPlaceholderImage() {
			return `${CONTENT_SERVER}/img/temp-bud2.jpg`
		},
		...mapGetters('cart', [
			'cart'
		]),
		...mapGetters('business', [
			'businessApiParam', 'businessId'
		]),
		price() {
			return getPrice({
				product: this.product,
				indication: this.indication
			})
		},
		hasPricing() {
			return hasPricing({
				product: this.product,
				indication: this.indication
			})
		},
		productRoute() {
			return {
				name: getProductRouteName(this.$route),
				params: {
					...this.$route.params,
					indication: this.indication,
					productId: this.product.menuid
				}
			}
		},
		hasBranding() {
			return !!this.product?.brand
		},
		pricePerUnit() {
			return getPricePerUnit({
				product: this.product,
				indication: this.indication
			})
		},
		pricingIsArray() {
			return pricingIsArray(this.product.pricing)
		},
		productPricing() {
			try {
				const pricing = this.product.pricing[this.indication]
				if (this.isArray(pricing)) {
					return pricing.filter(item => !!item[1])
				}
				if (this.isString(pricing) || this.isNumber(pricing)) {
					return pricing == 0 ? false : pricing // intentional coercion for string AND number check
				}
				return null
			} catch (error) {
				return null
			}
		},
		shouldShowEachLabel() {
			const { subType } = this.product
			return subType !== 'indica' &&
				subType !== 'sativa' &&
				subType !== 'hybrid' &&
				subType !== 'concentrates' &&
				subType !== 'tincture'
		},
		imageFile() {
			return (this.product?.image || this.product?.import_image) || false
		},
		hasNoPrice() {
			return typeof this.productPricing === 'string' && this.productPricing == '0' || false // intentional coercion for string AND number check
		},
		orderingDisabled() {
			return (this.product.has_ordering !== true || this.hasNoPrice) || false
		}
	},
	watch: {
		'cart.items': {
			deep: true,
			immediate: true,
			handler(newVal) {
				this.setPriceButtonState(newVal)
			}
		}
	},
	mounted() {
		this.isMounted = true
	},
	methods: {
		...mapMutations('modal', [ 'showModal' ]),
		setPriceButtonState(cartItemsArray) {
			this.sizesInCart = [] // reset array to initial state
			if (cartItemsArray && cartItemsArray.length) {
				// if an item in cart matches this product card id, return array of matching items.
				const matchingItemsArray = cartItemsArray.filter(item => item.product_id === this.product.menuid)
				if (matchingItemsArray && matchingItemsArray.length) { // if there are matching items in cart
					matchingItemsArray.forEach(item => { // then add each item's size to the sizesInCart Array.
						if (!this.sizesInCart.includes(item.size)) { // if the size is not already in the array
							this.sizesInCart.push(item.size) // add the size to the array
						}
					})
				} else { // if there are no matching items in cart
					this.sizesInCart = [] // clear the sizesInCart array
				}
			}
		},
		getPriceButtonBorderClasses() {
			return getPriceButtonBorderClasses({
				orderingDisabled: this.orderingDisabled,
				pricingIsArray: this.pricingIsArray,
				pricePerUnit: this.pricePerUnit
			})
		},
		formatUnit(unit) {
			return formatUnit(unit)
		},
		getDisabledButtonStyles(index, length) {
			if (index + 1 !== length) {
				return 'pointer-events-none dashed-border-right'
			}
			return 'pointer-events-none'
		},
		isString(pricing) {
			return typeof pricing === 'string'
		},
		isNumber(pricing) {
			return typeof pricing === 'number'
		},
		isArray(pricing) {
			return typeof pricing === 'object' && !!pricing.filter
		},
		async handleAddToCart({ index }) {
			this.itemBeingAddedToCart = index
			if (this.product.has_ordering === true && !this.sizesInCart.includes(index)) {
				this.size = index
				try {
					await this.addToCart({
						listing_id: this.businessId,
						items: [ {
							product_id: this.product.menuid,
							size: this.size,
							indication: this.indication,
							quantity: 1,
							name: this.product.name
						} ]
					})
				} catch (e) {
					Bugsnag.notify(e)
				}
			} else {
				this.stageItemToRemove(
					{
						listing_id: this.$store.state.business.listing_id,
						items: [ {
							product_id: this.product.menuid,
							size: index,
							indication: this.indication,
							quantity: 1,
							name: this.product.name,
							sizeLabel: this.price[index][0],
							price: this.price[index][1]
						} ]
					}
				)
				this.showModal(DELETE_FROM_CART_MODAL)
			}
			this.itemBeingAddedToCart = null
		},
		...mapActions('cart', [
			'addToCart',
			'stageItemToRemove'
		])
	}
}
</script>
<style lang="scss" scoped>
.right-position {
	right: -18px;
}
.price-button-min-width {
	min-width: 97px;
}
.input-border {
	border: solid 1px $gray5 !important;
}
.dashed-border-right {
	border-right: dashed 2px $gray4;
}
.dashed-border-right:last-of-type {
	border-right: none;
}
.max-w-300 {
	max-width: 300px;
}
.max-w-130 {
	max-width: 130px;
}
.min-w-116 {
	min-width: 116px;
}
.min-pricing-width {
	min-width: 190px;
}
.min-card-width {
	min-width: 280px;
}
</style>
